/**
 * Teacher-facing Clever publish modal.
 *
 * Design notes:
 * - The target classroom is pre-filled if the caller passes `classroomId`.
 *   Otherwise we render a dropdown of the teacher's classrooms so the action
 *   is reachable from the All-Lessons list view too.
 * - `grading_note` is collected for future use but NOT submitted in v1 —
 *   the backend doesn't persist it yet (ENGG-1767 followup). Kept here so
 *   teachers see their work echoed back rather than silently dropping it.
 * - Focus trap + ESC-to-close come from the shared `Modal` primitive; we
 *   only add an explicit initial focus to the classroom field so the form
 *   is immediately keyboard-usable.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { Classroom } from "@/types/tutor";
import Modal from "@/components/Modal";
import PrimaryButton from "@/components/PrimaryButton";
import SecondaryButton from "@/components/SecondaryButton";
import {
  NotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";
import { CleverNotConfiguredError, publishToClever } from "../api/clever";

interface CleverPublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessonId: number;
  lessonName: string;
  classrooms: Classroom[];
  // Pre-selected classroom id when launched from a classroom-scoped view.
  // When null/undefined the user picks from the dropdown.
  classroomId?: number | null;
  onPublishSuccess?: (classroomId: number) => void;
}

const DEFAULT_POINTS = 100;
const DEFAULT_MAX_ATTEMPTS = 1;

const CleverPublishModal = ({
  isOpen,
  onClose,
  lessonId,
  lessonName,
  classrooms,
  classroomId,
  onPublishSuccess,
}: CleverPublishModalProps) => {
  const { showNotification } = useNotificationStore();
  const classroomSelectRef = useRef<HTMLSelectElement>(null);

  const initialClassroomId = useMemo<number | "">(() => {
    if (classroomId != null) return classroomId;
    if (classrooms.length === 1) return classrooms[0].id;
    return "";
  }, [classroomId, classrooms]);

  const [selectedClassroomId, setSelectedClassroomId] = useState<number | "">(
    initialClassroomId,
  );
  const [dueAt, setDueAt] = useState<string>("");
  const [maxPoints, setMaxPoints] = useState<number>(DEFAULT_POINTS);
  const [maxAttempts, setMaxAttempts] = useState<number>(DEFAULT_MAX_ATTEMPTS);
  const [gradingNote, setGradingNote] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Re-sync form state when the caller opens the modal with a new context
  // (e.g., a different lesson). Without this, a second open reuses stale
  // user input from the previous session.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedClassroomId(initialClassroomId);
    setDueAt("");
    setMaxPoints(DEFAULT_POINTS);
    setMaxAttempts(DEFAULT_MAX_ATTEMPTS);
    setGradingNote("");
    setFormError(null);
    // Focus the classroom select if we have one to pick; otherwise Modal's
    // default focus trap will land on the first button.
    setTimeout(() => {
      if (
        classroomSelectRef.current &&
        document.activeElement !== classroomSelectRef.current &&
        classroomId == null
      ) {
        classroomSelectRef.current.focus();
      }
    }, 0);
  }, [isOpen, initialClassroomId, classroomId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (selectedClassroomId === "") {
      setFormError("Please choose a group to publish to.");
      return;
    }
    if (!Number.isFinite(maxPoints) || maxPoints < 0) {
      setFormError("Points possible must be a non-negative number.");
      return;
    }
    if (!Number.isFinite(maxAttempts) || maxAttempts < 1) {
      setFormError("Max attempts must be at least 1.");
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      // Convert the native datetime-local value ("YYYY-MM-DDTHH:mm") to an
      // ISO string with the user's timezone offset. The backend accepts any
      // ISO 8601 value and normalizes to UTC on persist.
      const dueAtIso = dueAt ? new Date(dueAt).toISOString() : undefined;
      const result = await publishToClever(
        selectedClassroomId as number,
        lessonId,
        {
          due_at: dueAtIso,
          max_points: maxPoints,
          max_attempts: maxAttempts,
          // grading_note intentionally not sent — server doesn't accept it
          // yet. See ENGG-1767.
        },
      );

      showNotification(
        `Published "${lessonName}" to Canvas (${result.sync_status}).`,
        NotificationType.SUCCESS,
      );
      onPublishSuccess?.(selectedClassroomId as number);
      onClose();
    } catch (error) {
      if (error instanceof CleverNotConfiguredError) {
        setFormError(
          "Clever integration is not configured for this environment.",
        );
      } else {
        const detail =
          (error as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ?? "Failed to publish to Canvas. Please try again.";
        setFormError(detail);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} showCloseButton>
      <form
        onSubmit={handleSubmit}
        className="p-2 md:p-4 space-y-5"
        aria-label="Publish lesson to Canvas"
      >
        <header className="space-y-1">
          <h2 className="text-2xl font-bold text-black font-mulish">
            Publish to Canvas
          </h2>
          <p className="text-sm text-gray-600">
            Send <span className="font-semibold">{lessonName}</span> to Canvas
            via Clever LMS Connect.
          </p>
        </header>

        <div>
          <label
            htmlFor="clever-publish-classroom"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Target group
          </label>
          <select
            id="clever-publish-classroom"
            ref={classroomSelectRef}
            value={selectedClassroomId}
            onChange={(e) =>
              setSelectedClassroomId(
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
            disabled={isSubmitting || classroomId != null}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#28543D] focus:border-transparent disabled:bg-gray-100"
            required
          >
            <option value="">Choose a group…</option>
            {classrooms.map((classroom) => (
              <option key={classroom.id} value={classroom.id}>
                {classroom.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="clever-publish-due-at"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Due date (optional)
            </label>
            <input
              id="clever-publish-due-at"
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              disabled={isSubmitting}
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#28543D] focus:border-transparent"
            />
          </div>
          <div>
            <label
              htmlFor="clever-publish-points"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Points possible
            </label>
            <input
              id="clever-publish-points"
              type="number"
              min={0}
              step={1}
              value={maxPoints}
              onChange={(e) => setMaxPoints(Number(e.target.value))}
              disabled={isSubmitting}
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#28543D] focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="clever-publish-attempts"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Max attempts
          </label>
          <input
            id="clever-publish-attempts"
            type="number"
            min={1}
            step={1}
            value={maxAttempts}
            onChange={(e) => setMaxAttempts(Number(e.target.value))}
            disabled={isSubmitting}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#28543D] focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Stored locally for analytics. Canvas displays unlimited attempts.
          </p>
        </div>

        <div>
          <label
            htmlFor="clever-publish-note"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Grading note (optional)
          </label>
          <textarea
            id="clever-publish-note"
            value={gradingNote}
            onChange={(e) => setGradingNote(e.target.value)}
            disabled={isSubmitting}
            rows={3}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#28543D] focus:border-transparent resize-none"
            placeholder="Notes for your own records."
          />
          <p className="text-xs text-gray-500 mt-1">
            Stays on this device for now; synced to Canvas in a future release.
          </p>
        </div>

        {formError != null && (
          <div
            role="alert"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
          >
            {formError}
          </div>
        )}

        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
          <SecondaryButton
            onClick={handleClose}
            size="md"
            mobileFullWidth
            disabled={isSubmitting}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            size="md"
            mobileFullWidth
            disabled={isSubmitting}
          >
            {isSubmitting ? "Publishing…" : "Publish to Canvas"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
};

export default CleverPublishModal;
